import React from 'react';
import { TextField, Select, MenuItem, FormControl, InputLabel, Typography, Grid, CircularProgress } from '@mui/material';
import { useCallback, useEffect, useState, useRef } from 'react';
import _ from 'lodash';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import ComponentHeaderTitles from '../ComponentHeaderTitles';
/**
 * @file This file contains the AdresKiezer component which is used to select an address by postcode and house number.
 * @module AdresKiezer
 */

/**
 * AdresKiezer component.
 * @param {Object} props - The component props.
 * @param {Function} props.setPostcode - Function to set the postcode state in the parent component.
 * @param {Function} props.setHousenumber - Function to set the house number state in the parent component.
 * @param {Function} props.onValidationChange - Function to handle validation changes in the parent component.
 * @param {Function} props.setAvailableMeterTypes - Function to set the available meter types in the parent component.
 * @param {Function} props.setHousenumberSuffix - Function to set the house number suffix state in the parent component.
 * @param {Function} props.setStreetName - Function to set the street name state in the parent component.
 * @param {Function} props.setCityName - Function to set the city name state in the parent component.
 * @param {Function} props.onUpdateValidationErrors - Function to update the validation errors in the parent component.
 * @param {String} props.postcode - The postcode state from the parent component.
 * @param {String} props.housenumber - The house number state from the parent component.
 * @param {String} props.suffix - The house number suffix state from the parent component.
 * @param {String} props.streetName - The street name state from the parent component.
 * @param {String} props.cityName - The city name state from the parent component.
 * @param {Object} props.housenumberSuffixList - The housenumberSuffixList state from the parent component.
 * @param {Function} props.setHousenumberSuffixList - Function to set the housenumberSuffixList state in the parent component.
 * @param {Function} props.setIsFetchingData - Function to set the isFetchingData state in the parent component.
 * 
 * @returns {JSX.Element} The AdresKiezer component.
 */
const AdresKiezer = ({ setIsFetchingData, postcode, housenumber, suffix, setPostcode, setHousenumber,
    onValidationChange, setAvailableMeterTypes, housenumberSuffixList, setHousenumberSuffixList, sethouseNumberSuffixesOnAddress,
    setHousenumberSuffix, setStreetName, setCityName, onUpdateValidationErrors, streetName, cityName,
    isPostCodeAndHouseNumberFormatValid, setIsPostCodeAndHouseNumberFormatValid    
}) => {
    // set state to empty string if postcode and housenumber are not set, otherwise set state to the value of postcode and housenumber
    const [housenumberSuffixOptions, setHousenumberSuffixOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [customSuffix, setCustomSuffix] = useState(''); // State to hold the custom suffix
    const [isCustomSuffix, setIsCustomSuffix] = useState(false); // State to toggle the visibility of the custom input field
    const [editingCustomSuffix, setEditingCustomSuffix] = useState(false);
    const [isAddressValid, setIsAddressValid] = useState(false);


    const [street, setStreet] = useState('');
    const [city, setCity] = useState('');
    const [streetNumber, setStreetNumber] = useState('');

    const [postcodeError, setPostcodeError] = useState(false);
    const [postcodeHelperText, setPostcodeHelperText] = useState('');
    const [housenumberError, setHousenumberError] = useState(false);
    const [housenumberHelperText, setHousenumberHelperText] = useState('');

    
    /**
     * Used to ensure that when the user comes back to this component, the address is still valid and as such
     * the suffix field is still enabled.
     */
    useEffect(() => {
        if (isPostCodeAndHouseNumberFormatValid) {
            setIsAddressValid(true);
        } else {
            setIsAddressValid(false);
        }
    }, [isPostCodeAndHouseNumberFormatValid]);





    const formatCityName = (cityName) => {
        return cityName.replace(/\b(\w)/g, s => s.toUpperCase()).replace(/\B(\w)/g, s => s.toLowerCase());
    };



    const BACKEND_URL = "https://offerte.api.cleanenergy.nl/getAddressNumbers";

    const handleHousenumberSuffixChange = (event) => {
        const value = event.target.value;
        if (value === "Anders") {
            setIsCustomSuffix(true);
            setEditingCustomSuffix(true);
            setCustomSuffix('');
        } else {
            setIsCustomSuffix(false);
            setEditingCustomSuffix(false);
            // Check if the value is 'Geen', if so, set the suffix to null. Otherwise, set the suffix to the selected value.
            setHousenumberSuffix(value == "Geen" ? null : value);
        }
        validateForm(postcode, housenumber);
    };




    const handlePostcodeChange = (event) => {
        if (preventIllegalInput(event)) {
            return;
        }
        const value = event.target.value.toUpperCase();
        setPostcode(value); // Update de state in de parent component
        const isValid = validatePostcode(value);
        setPostcodeError(!isValid[0]); // Update de error state
        if (!isValid[0]) {
            onUpdateValidationErrors([isValid[1], false]);
        } else {
            onUpdateValidationErrors([]); // Reset errors als de postcode geldig is
        }
    };


    /**
         * @function preventIllegalInput
         * @param {*} event
         * @description This function prevents the user from entering illegal characters in the postcode field..
         * @returns {boolean} - Returns true if the input is illegal, false otherwise.
         */
    function preventIllegalInput(event) {
        var inputValue = event.nativeEvent.data;
        var selectionStart = event.target.selectionStart;
        var illegalCharacter = false;
        // if the position of event character is in the first 4 characters of postcode and is a alphabetic character
        if (selectionStart < 5 && isNaN(inputValue)) {
            // remove character from input field
            event.target.value = event.target.value.slice(0, -1);
            illegalCharacter = true;
            return;
        }

        // if the position of event character is in the last 2 characters of postcode and is not a letter
        else if (selectionStart >= 5 && !/^[a-zA-Z]+$/.test(inputValue)) {
            // remove character from input field
            event.target.value = event.target.value.slice(0, -1);
            illegalCharacter = true;
            return;
        } else {
            illegalCharacter = false;
        }


        return illegalCharacter;
    }

    const handleHousenumberChange = (event) => {
        const value = event.target.value;
        // Regular expression to match only numeric values.
        // Without this check, the user can enter the 'e' character.
        const re = /^[0-9\b]+$/;
        // If value is empty or passes the regex test, update the state
        if (value === '' || re.test(value)) {
            setHousenumber(value); // Update the parent component's state
            let isValid = validateForm(postcode, value);
            if (isValid) {
                onUpdateValidationErrors([]); // Reset errors if the housenumber is valid
            } 
        }
    };




    const validatePostcode = (postcode) => {
        const regex = /^\d{4}[A-Za-z]{2}$/;
        var isvalid = regex.test(postcode);
        if (!isvalid) {
            setPostcodeError(true); // Zet het error-vlag
            setPostcodeHelperText('Postcode moet een geldige postcode zijn'); // Update helper text/error message
            return [false, 'Postcode moet een geldige postcode zijn'];
        } else {
            setPostcodeError(false); // Verwijder het error-vlag
            setPostcodeHelperText(''); // Verwijder de foutmelding
            return [true, null];
        }
    };

    const validateHouseNumber = (housenumber) => {
        // check if housenumber is a number
        if (isNaN(housenumber) || housenumber === "") {
            setHousenumberError(true); // Zet het error-vlag
            setHousenumberHelperText('Huisnummer moet een nummer zijn'); // Update helper text/error message
            return [false, 'Huisnummer moet een nummer zijn'];
        }
        setHousenumberError(false); // Verwijder het error-vlag
        setHousenumberHelperText(''); // Verwijder de foutmelding
        return [true, null];
    };

    const fieldsAreFilled = () => {
        return postcode !== '' && housenumber !== '';
    };


    /**
         * @function handleInput
         * @description This function is called when there is an input event on the postcode or house number fields. It waits for 500 milliseconds before making an HTTP request to the backend API to get the available house number suffix options.
         */
    const debouncedHandleInput = useCallback(_.debounce(async () => {
        // Clear the previous debounce timer
        if (!fieldsAreFilled()) {
            return;
        }
        // Set a new debounce timer to wait for 500 milliseconds so that the user can finish typing
        // in their input before making an HTTP request
        // When both fields are filled, make an HTTP request
        // const postcode = $('#normal-form-postcode').val();
        // const housenumber = $('#huisnummer').val();
        setLoading(true);
        setIsFetchingData(true);
        // Get the Toevoeging options from the backend
        const url = new URL(BACKEND_URL);
        const params = { postcode: postcode, housenumber: housenumber };
        url.search = new URLSearchParams(params).toString();

        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            })
            if (response.ok) {
                const data = await response.json();



                setHousenumberSuffixOptions(data.additions);
                sethouseNumberSuffixesOnAddress(data.additions);
                setAvailableMeterTypes(data.meters)
                if (data.meters && data.meters[0] && data.meters[0].address && data.meters[0].address.street && data.meters[0].address.city) {
                    setStreetName(data.meters[0].address.street);
                    setCityName(formatCityName(data.meters[0].address.city));
                    setStreet(data.meters[0].address.street);
                    setCity(formatCityName(data.meters[0].address.city));
                    setStreetNumber(data.meters[0].address.streetNumber);
                } else {
                    setStreetName(null);
                    setCityName(null);
                    setStreet(null);
                    setCity(null);
                    setStreetNumber(null);
                }
                setHousenumberSuffixList({
                    postcode: postcode,
                    housenumber: housenumber,
                    suffixList: data.additions
                });
            } else {
                console.error('Error fetching housenumber suffix options', response);
            }
        } catch (error) {
            console.error('Error fetching housenumber suffix options', error);
        } finally {
            setLoading(false);
            setIsFetchingData(false);
        }

    }, 500), [postcode, housenumber]); // Dependencies

    const isFirstRender = useRef(true);

    useEffect(() => {
        // Skip the effect on the initial render
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        // Your data fetching logic here
        debouncedHandleInput();

        // Cleanup logic
        return () => {
            debouncedHandleInput.cancel();
        };
    }, [debouncedHandleInput]);

    // Update the customSuffix state when the user types in the custom input field
    const handleCustomSuffixChange = (event) => {
        const value = event.target.value;
        setCustomSuffix(value);
        setEditingCustomSuffix(true);

        // Check if there is a custom value, if so, use it, otherwise set to 'Geen'
        if (value.trim() !== '') {
            setHousenumberSuffix(value); // Update the suffix with the custom value
        } else {
            // If the custom field is empty, revert to 'Geen'
            setHousenumberSuffix('Geen');
        }
    };

    const handleExitCustomSuffix = () => {
        setCustomSuffix('');
        setIsCustomSuffix(false);
        setEditingCustomSuffix(false);
        setHousenumberSuffix('Geen'); // or another default value as needed
    };


    useEffect(() => {
        // If there is a saved suffix and it is not part of the standard options,
        // or if the user is actively editing the custom suffix.
        if ((suffix && !housenumberSuffixOptions.includes(suffix)) || editingCustomSuffix) {
            setIsCustomSuffix(true);
            // If it's a custom suffix, set the customSuffix state
            if (suffix && !housenumberSuffixOptions.includes(suffix)) {
                setCustomSuffix(suffix);
            }
        } else {
            setIsCustomSuffix(false);
        }



    }, [housenumberSuffixOptions, suffix, editingCustomSuffix]);




    useEffect(() => {
        if (housenumberSuffixList.postcode === postcode && housenumberSuffixList.housenumber === housenumber) {
            const updatedOptions = ['Geen', ...housenumberSuffixList.suffixList || []];
            setHousenumberSuffixOptions(updatedOptions);
            validateForm(postcode, housenumber);
            // If we have a custom suffix, display the custom input with that value.
            if (suffix && !updatedOptions.includes(suffix)) {
                setIsCustomSuffix(true);
                setEditingCustomSuffix(false); // The user is not actively editing, but we're showing the saved custom value
                setCustomSuffix(suffix);
                setHousenumberSuffix(suffix);
            } else if (!editingCustomSuffix) {
                // Standard suffix selected from the list
                const suffixToSet = updatedOptions.includes(suffix) ? suffix : 'Geen';
                setHousenumberSuffix((suffixToSet === 'Geen' ? null : suffixToSet));
            }
        } else {
            if (fieldsAreFilled()) {
                setHousenumberSuffixOptions(['Geen']);
                if (!editingCustomSuffix) {
                    setHousenumberSuffix('Geen');
                }
                debouncedHandleInput();
            }
        }
    }, [postcode, housenumber, housenumberSuffixList, suffix, debouncedHandleInput, editingCustomSuffix]);






    
    
    const validateForm = (postcode, housenumber) => {
        let errorMessages = [];
        const [postcodeIsValid, postcodeErrorMessage] = validatePostcode(postcode.trim());
        const [housenumberIsValid, huisnummerErrorMessage] = validateHouseNumber(housenumber.trim());
        errorMessages.push(postcodeErrorMessage, huisnummerErrorMessage);
        const isValid = postcodeIsValid && housenumberIsValid;

        setIsAddressValid(isValid); // Set the validity state based on postcode and housenumber validation
        setIsPostCodeAndHouseNumberFormatValid(isValid);

        onValidationChange({ isValid, errorMessages });
        return isValid;
    };




    return (
        <div>
            <ComponentHeaderTitles title="Op welk adres mogen wij energie leveren?" />
            <div className="adres-kiezer">
                <div className="postcode" id="normal-form-postcode">
                    <Typography variant="subtitle1" color="textPrimary">
                        <b>Postcode</b>
                    </Typography>
                    <TextField
                        placeholder="1234AB"
                        type="text"
                        value={postcode}
                        onChange={handlePostcodeChange}
                        className="postcode-input"
                        inputProps={{ maxLength: 6 }}
                        // onBlur={() => validateForm(postcode, housenumber)}
                        error={postcodeError} // Gebruik de error state
                        helperText={postcodeHelperText} // Gebruik de helperText state

                    />
                </div>

                <div className="housenumber">
                    <Typography variant="subtitle1" color="textPrimary">
                        <b>Huisnummer</b>
                    </Typography>
                    <TextField
                        placeholder="0"
                        type="text" // This is a text field instead of a number type, otherwise the user is able to enter the 'e' character.
                        value={housenumber}
                        onChange={handleHousenumberChange}
                        onBlur={() => {
                            validateHouseNumber(housenumber);
                          }}
                        error={housenumberError} // Gebruik de error state
                        helperText={housenumberHelperText} // Gebruik de helperText state
                        className='housenumber-input'
                        inputProps={{
                            inputMode: "numeric", // This will still bring up the numeric keypad on mobile devices
                        }}
                    />
                </div>
                <div className="suffix">
                    <Typography variant="subtitle1" color="textPrimary">
                        <b>Toev.</b>
                    </Typography>
                    {isCustomSuffix && (
                        <TextField
                            placeholder="Voer uw toevoeging in"
                            type="text"
                            value={customSuffix}
                            onChange={handleCustomSuffixChange}
                            className="custom-suffix-input"
                            disabled={!isAddressValid} // Disable the input based on isAddressValid
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={handleExitCustomSuffix}
                                            edge="end"
                                        >
                                            <CloseIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    )}
                    {!isCustomSuffix && (
                        <FormControl style={{ width: '100px' }} disabled={!isAddressValid}>
                            <Select
                                value={suffix}
                                className='suffix-input'
                                onChange={handleHousenumberSuffixChange}
                            >

                                {
                                    housenumberSuffixOptions
                                        .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
                                        .map((option) => (
                                            <MenuItem key={option} value={option}>
                                                {option}
                                            </MenuItem>
                                        ))
                                }

                                <MenuItem value="Anders">Anders</MenuItem>
                            </Select>
                            {loading && <CircularProgress size={40} className='spinner' color="secondary" thickness={5} />}
                        </FormControl>

                    )}
                </div>
                {/* Weergave van straat en stad onder de invoervelden */}
                {(streetName || street) && (cityName || city) && (
                    <div className="adres-info">
                        <Typography variant="subtitle1" color="textPrimary">
                            <svg fill="#000000" height="15px" width="15px" version="1.1" id="Capa_1"
                                viewBox="0 0 297 297">
                                <g>
                                    <path d="M148.5,0C87.43,0,37.747,49.703,37.747,110.797c0,91.026,99.729,179.905,103.976,183.645
		c1.936,1.705,4.356,2.559,6.777,2.559c2.421,0,4.841-0.853,6.778-2.559c4.245-3.739,103.975-92.618,103.975-183.645
		C259.253,49.703,209.57,0,148.5,0z M148.5,272.689c-22.049-21.366-90.243-93.029-90.243-161.892
		c0-49.784,40.483-90.287,90.243-90.287s90.243,40.503,90.243,90.287C238.743,179.659,170.549,251.322,148.5,272.689z"/>
                                    <path d="M148.5,59.183c-28.273,0-51.274,23.154-51.274,51.614c0,28.461,23.001,51.614,51.274,51.614
		c28.273,0,51.274-23.153,51.274-51.614C199.774,82.337,176.773,59.183,148.5,59.183z M148.5,141.901
		c-16.964,0-30.765-13.953-30.765-31.104c0-17.15,13.801-31.104,30.765-31.104c16.964,0,30.765,13.953,30.765,31.104
		C179.265,127.948,165.464,141.901,148.5,141.901z"/>
                                </g>
                            </svg>
                            {streetName || street} {housenumber || streetNumber} {suffix} {cityName || city}
                        </Typography>
                    </div>
                )}
            </div>
        </div>
    );
};

AdresKiezer.displayName = 'AdresKiezer';
export default AdresKiezer;
